import { getAllPermissionActions, type permissionManifest } from "@msuite/citadel";
import { localized } from "@msuite/ui/localization";
import { createColumnHelper, createMeta } from "@msuite/ui/table";
import { localizedAction, localizedSubject } from "@/components/citadel";
import { grantKey } from "./grants";
import { ScopeCell } from "./scope-cell";

/** One row of the editor: a manifest subject, keyed by itself. */
export type PermissionRow = { id: string } & (typeof permissionManifest)[number];

const columnHelper = createColumnHelper<PermissionRow>();

/** Props Interface */
interface CreateColumnsOptions {
	/** The pairs the role holds under a template the scope map does not know. */
	customKeys: Set<string>;
	/** Set for the `admin` role, whose grants nobody can change. */
	isDisabled: boolean;
}

/**
 * The editor's columns: the subject, then one per action the manifest declares anywhere. Built
 * per render rather than at module evaluation, so the labels follow a language switch — the
 * static column configs elsewhere in the app do not.
 */
export function createColumns({ customKeys, isDisabled }: CreateColumnsOptions) {
	return [
		columnHelper.accessor("subject", {
			id: "subject",
			header: localized("permissions.permission"),
			cell: (cell) => localizedSubject(cell.getValue()),
		}),
		...getAllPermissionActions().map((action) =>
			columnHelper.display({
				id: action,
				header: localizedAction(action),
				// The cell is the select: the embedded variant fills it edge to edge, so the cell
				// padding would only draw a dead margin around the control.
				meta: createMeta({ withoutPadding: true }),
				cell: (cell) => (
					<ScopeCell
						item={cell.row.original}
						action={action}
						isCustom={customKeys.has(grantKey(action, cell.row.original.subject))}
						isDisabled={isDisabled}
					/>
				),
			}),
		),
	];
}
